import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Link2, Copy, Check, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QRAutorregistro = () => {
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const [copied, setCopied] = React.useState(false);
  
  // URL pública para el QR (dominio principal)
  const qrUrl = `${process.env.REACT_APP_DOMINIO_PRINCIPAL}/aquiempiezatutrajeconjuanpablomurillo`;

  // Copiar enlace al portapapeles
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  // Descargar QR como PNG
  const handleDownloadPNG = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    // Crear canvas con padding y branding
    const size = 1200;
    const qrSize = 800;
    const padding = (size - qrSize) / 2;

    img.onload = () => {
      canvas.width = size;
      canvas.height = size + 200; // Espacio extra para texto

      // Fondo oscuro elegante
      ctx.fillStyle = '#18181b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Fondo blanco para el QR
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(padding - 40, padding - 40, qrSize + 80, qrSize + 80);

      // Dibujar QR
      ctx.drawImage(img, padding, padding, qrSize, qrSize);

      // Texto superior
      ctx.fillStyle = '#ffffff';
      ctx.font = '600 48px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('JUAN PABLO MURILLO', size / 2, size + 80);

      // Subtexto
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '400 28px system-ui, sans-serif';
      ctx.fillText('Escanea para registrarte', size / 2, size + 130);

      // Descargar
      const link = document.createElement('a');
      link.download = 'QR-Autorregistro-JPM.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Descargar QR como SVG
  const handleDownloadSVG = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgClone = svg.cloneNode(true);
    svgClone.setAttribute('width', '800');
    svgClone.setAttribute('height', '800');

    const svgData = new XMLSerializer().serializeToString(svgClone);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.download = 'QR-Autorregistro-JPM.svg';
    link.href = url;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        
        <h1 className="text-2xl md:text-3xl font-semibold text-white mb-2">
          Código QR de Autorregistro
        </h1>
        <p className="text-zinc-400">
          Descarga el código QR para usar en tienda, showroom, tarjetas de presentación o redes sociales.
        </p>
      </div>

      {/* QR Card */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800">
          {/* QR Preview */}
          <div className="flex flex-col items-center mb-8">
            <div 
              ref={qrRef}
              className="bg-white p-6 rounded-2xl shadow-xl"
            >
              <QRCodeSVG
                value={qrUrl}
                size={280}
                level="H"
                includeMargin={false}
                fgColor="#18181b"
                bgColor="#ffffff"
              />
            </div>
            
            <div className="mt-6 text-center">
              <h2 className="text-xl font-semibold text-white tracking-wider">
                JUAN PABLO MURILLO
              </h2>
              <p className="text-zinc-500 text-sm mt-1">
                Escanea para registrarte
              </p>
            </div>
          </div>

          {/* URL Info */}
          <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
              <Link2 className="w-4 h-4" />
              <span>Enlace del formulario:</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs md:text-sm text-emerald-400 bg-zinc-800 px-3 py-2 rounded-lg overflow-x-auto">
                {qrUrl}
              </code>
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm transition-colors"
                title="Copiar enlace"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="hidden sm:inline">Copiado</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span className="hidden sm:inline">Copiar</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Download Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={handleDownloadPNG}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-zinc-900 rounded-xl font-semibold hover:bg-zinc-100 transition-colors"
            >
              <Download className="w-5 h-5" />
              Descargar PNG
            </button>
            <button
              onClick={handleDownloadSVG}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-zinc-700 text-white rounded-xl font-semibold hover:bg-zinc-600 transition-colors"
            >
              <Download className="w-5 h-5" />
              Descargar SVG
            </button>
          </div>

          {/* Tips */}
          <div className="mt-6 pt-6 border-t border-zinc-800">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">
              Sugerencias de uso:
            </h3>
            <ul className="text-sm text-zinc-500 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-emerald-500">•</span>
                <span>Imprime en tamaño grande para el mostrador del showroom</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500">•</span>
                <span>Incluye en tarjetas de presentación o folletos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500">•</span>
                <span>Comparte en redes sociales (Instagram stories, WhatsApp)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500">•</span>
                <span>Usa el SVG para materiales de alta calidad de impresión</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRAutorregistro;
